import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// ============================================================
// RASMUSTEN — Fantasy Mission RPG
// ============================================================

// ---------- Races / Heroes ----------
// Core stats: STR (strength), AGI (agility), INT (intellect), VIT (vitality)
const RACES = [
  { id:"orc",    name:"Orc Berserker",   img:"assets/heroes/orc.jpg",    base:{ str:14, agi:7,  int:4,  vit:12 }, color:"#7fd17f",
    blurb:"Towering green-skinned brutes. Unmatched raw Strength and Vitality." },
  { id:"undead", name:"Undead Revenant",  img:"assets/heroes/undead.jpg", base:{ str:9,  agi:9,  int:13, vit:6  }, color:"#9bd4ff",
    blurb:"Risen from the grave. Masters of dark Intellect and necromancy." },
  { id:"elf",    name:"High Elf Ranger",  img:"assets/heroes/elf.jpg",    base:{ str:6,  agi:14, int:11, vit:6  }, color:"#c084fc",
    blurb:"Graceful and ancient. Superb Agility and arcane attunement." },
  { id:"human",  name:"Human Knight",     img:"assets/heroes/human.jpg",  base:{ str:10, agi:10, int:9,  vit:10 }, color:"#ffd76b",
    blurb:"Versatile and bold. Balanced across every attribute." },
  { id:"furry",  name:"Lionfolk Warden",  img:"assets/heroes/furry.jpg",  base:{ str:12, agi:11, int:5,  vit:9  }, color:"#ff9a1a",
    blurb:"Proud beast-warriors. Ferocious Strength paired with feline Agility." },
];

const CORE_STATS = {
  str: { n:"Strength",  i:"💪", c:"#ff7a6b", d:"Boosts Attack & physical damage" },
  agi: { n:"Agility",   i:"🏹", c:"#7fe8b0", d:"Boosts Speed, crit & evasion" },
  int: { n:"Intellect", i:"✦",  c:"#c89bff", d:"Boosts Magic & mana power" },
  vit: { n:"Vitality",  i:"❤", c:"#ff5577", d:"Boosts Defense & health" },
};

// ---------- Rarity ----------
const RARITIES = {
  common:    { name:"Common",    color:"#b8b8b8", weight:50,  mult:1.0 },
  uncommon:  { name:"Uncommon",  color:"#4ade80", weight:28,  mult:1.6 },
  rare:      { name:"Rare",      color:"#3b9dff", weight:14,  mult:2.6 },
  epic:      { name:"Epic",      color:"#c084fc", weight:6.5, mult:4.2 },
  legendary: { name:"Legendary", color:"#ff9a1a", weight:1.5, mult:7.0 },
};
const RARITY_ORDER = ["common","uncommon","rare","epic","legendary"];

// ---------- Equipment slots (with on-hero layer position) ----------
const SLOTS = [
  { id:"weapon", name:"Weapon",    icon:"⚔️", main:"str", layer:{ b:"6%",  r:"8%",  s:"34%", rot:"12deg", z:6 } },
  { id:"head",   name:"Helm",      icon:"🪖", main:"vit", layer:{ t:"4%",  l:"50%", s:"30%", tr:"translateX(-50%)", z:7 } },
  { id:"chest",  name:"Armor",     icon:"🛡️", main:"vit", layer:{ t:"40%", l:"50%", s:"40%", tr:"translateX(-50%)", z:5 } },
  { id:"hands",  name:"Gauntlets", icon:"🧤", main:"str", layer:{ b:"14%", l:"6%",  s:"24%", z:6 } },
  { id:"legs",   name:"Greaves",   icon:"👖", main:"vit", layer:{ b:"2%",  l:"50%", s:"30%", tr:"translateX(-50%)", z:4 } },
  { id:"ring",   name:"Ring",      icon:"💍", main:"int", layer:{ b:"16%", r:"22%", s:"14%", z:6 } },
  { id:"amulet", name:"Amulet",    icon:"📿", main:"int", layer:{ t:"34%", l:"50%", s:"18%", tr:"translateX(-50%)", z:6 } },
  { id:"boots",  name:"Boots",     icon:"🥾", main:"agi", layer:{ b:"0%",  r:"30%", s:"20%", z:5 } },
];

const ITEM_NAMES = {
  weapon:["Iron Blade","Oakheart Bow","Runed Mace","Shadowfang Dagger","Stormcaller Staff","Dragonbone Greatsword","Whisperwind Saber","Emberforge Axe"],
  head:["Leather Cap","Bronze Helm","Wardens Visor","Crown of Embers","Spirit Hood","Helm of the Ancients"],
  chest:["Padded Vest","Chainmail","Platebody of Valor","Robe of the Arcane","Drakescale Cuirass","Bulwark of Rasmusten"],
  hands:["Cloth Wraps","Iron Gauntlets","Grips of Fury","Touch of Frost","Titanforged Gloves"],
  legs:["Worn Trousers","Steel Greaves","Legguards of Might","Stride of Shadows","Colossus Legplates"],
  ring:["Copper Band","Sapphire Ring","Loop of Vigor","Signet of Power","Eternity Coil"],
  amulet:["Bone Charm","Jade Pendant","Talisman of Light","Heart of the Forest","Soul of Rasmusten"],
  boots:["Hide Sandals","Swift Boots","Treads of Haste","Windwalkers","Boots of the Phoenix"],
};

const MISSIONS = [
  { id:"m1", name:"Patrol the Verdant Glade", tier:1, minutes:0.5, gold:[20,40], lvl:1, img:"assets/missions/m1.jpg", desc:"Clear stray wolves from the village outskirts." },
  { id:"m2", name:"The Sunken Crypt", tier:1, minutes:1, gold:[35,60], lvl:2, img:"assets/missions/m2.jpg", desc:"Investigate restless dead beneath the chapel." },
  { id:"m3", name:"Bandits of Hollow Road", tier:2, minutes:2, gold:[60,110], lvl:4, img:"assets/missions/m3.jpg", desc:"Ambush the highwaymen plaguing the trade route." },
  { id:"m4", name:"Frostpeak Expedition", tier:2, minutes:4, gold:[110,190], lvl:7, img:"assets/missions/m4.jpg", desc:"Brave the blizzard to reach the frozen shrine." },
  { id:"m5", name:"The Ashen Mire", tier:3, minutes:7, gold:[200,340], lvl:11, img:"assets/missions/m5.jpg", desc:"Burn out the spider nests in the poisoned swamp." },
  { id:"m6", name:"Siege of Blackgate", tier:3, minutes:11, gold:[360,560], lvl:16, img:"assets/missions/m6.jpg", desc:"Storm the orc warcamp at the eastern wall." },
  { id:"m7", name:"Tomb of the Lich King", tier:4, minutes:16, gold:[620,980], lvl:22, img:"assets/missions/m7.jpg", desc:"Descend into the cursed catacombs of Rasmusten." },
  { id:"m8", name:"Wrath of the Sky Dragon", tier:4, minutes:24, gold:[1100,1800], lvl:30, img:"assets/missions/m8.jpg", desc:"Face the ancient wyrm atop Mount Caldron." },
  { id:"m9", name:"The Void Rift", tier:5, minutes:35, gold:[2200,3600], lvl:42, img:"assets/missions/m9.jpg", desc:"Seal the tear in reality before it consumes the realm." },
];
const TIER_COLORS = { 1:"#7fd17f", 2:"#5bb8e8", 3:"#c084fc", 4:"#ff9a1a", 5:"#ff5577" };

const ACHIEVEMENTS = [
  { id:"first_mission", name:"First Steps", desc:"Complete your first mission.", crystals:3, check:s=>s.stats.missions>=1 },
  { id:"five_missions", name:"Seasoned Adventurer", desc:"Complete 5 missions.", crystals:3, check:s=>s.stats.missions>=5 },
  { id:"twenty_missions", name:"Veteran of Rasmusten", desc:"Complete 20 missions.", crystals:5, check:s=>s.stats.missions>=20 },
  { id:"fifty_missions", name:"Legend of the Realm", desc:"Complete 50 missions.", crystals:10, check:s=>s.stats.missions>=50 },
  { id:"gold_1k", name:"Coin Collector", desc:"Earn 1,000 gold total.", crystals:3, check:s=>s.stats.goldEarned>=1000 },
  { id:"gold_10k", name:"Wealthy Hero", desc:"Earn 10,000 gold total.", crystals:5, check:s=>s.stats.goldEarned>=10000 },
  { id:"gold_100k", name:"Dragon's Hoard", desc:"Earn 100,000 gold total.", crystals:12, check:s=>s.stats.goldEarned>=100000 },
  { id:"first_legendary", name:"The Chosen", desc:"Loot a Legendary item.", crystals:8, check:s=>s.stats.legendaries>=1 },
  { id:"full_gear", name:"Fully Equipped", desc:"Equip an item in every slot.", crystals:5, check:s=>SLOTS.every(sl=>s.equipped[sl.id]) },
  { id:"power_500", name:"Mighty", desc:"Reach 500 Power.", crystals:5, check:(s,p)=>p>=500 },
  { id:"power_2000", name:"Unstoppable", desc:"Reach 2,000 Power.", crystals:10, check:(s,p)=>p>=2000 },
  { id:"level_10", name:"Battle-Hardened", desc:"Reach Level 10.", crystals:4, check:s=>s.level>=10 },
  { id:"level_25", name:"Champion", desc:"Reach Level 25.", crystals:8, check:s=>s.level>=25 },
  { id:"speedup", name:"Impatient", desc:"Speed up a mission with crystals.", crystals:2, check:s=>s.stats.speedups>=1 },
  { id:"void_clear", name:"Realm Saver", desc:"Complete The Void Rift.", crystals:20, check:s=>s.stats.voidClears>=1 },
];

const CRYSTAL_PACKS = [
  { id:"p1", crystals:50, oneTime:true, label:"Claim 50 free crystals (one-time)" },
  { id:"p2", crystals:25,  goldCost:2500,  label:"Exchange 2,500 gold" },
  { id:"p3", crystals:80,  goldCost:7000,  label:"Exchange 7,000 gold" },
  { id:"p4", crystals:200, goldCost:15000, label:"Exchange 15,000 gold (bonus!)" },
];

// ---------- Helpers ----------
const rand=(a,b)=>Math.floor(Math.random()*(b-a+1))+a;
const uid=()=>Math.random().toString(36).slice(2,10);
const clamp=(x,a,b)=>Math.max(a,Math.min(b,x));

function rollRarity(tierBonus=0){
  const entries=RARITY_ORDER.map(k=>{ let w=RARITIES[k].weight; if(k!=="common"&&k!=="uncommon") w*=(1+tierBonus*0.5); return [k,w]; });
  const total=entries.reduce((a,[,w])=>a+w,0); let r=Math.random()*total;
  for(const [k,w] of entries){ if((r-=w)<=0) return k; } return "common";
}
function generateItem(mission){
  const slot=SLOTS[rand(0,SLOTS.length-1)];
  const rarity=rollRarity(mission.tier-1);
  const names=ITEM_NAMES[slot.id];
  const base=names[Math.min(rand(0,names.length-1)+Math.floor((mission.tier-1)/1.5),names.length-1)];
  const ilvl=mission.lvl+rand(0,3);
  const budget=Math.round(ilvl*RARITIES[rarity].mult*(2+Math.random()*1.5));
  const stats={}; stats[slot.main]=budget;
  if(rarity!=="common"){ const secs=Object.keys(CORE_STATS).filter(x=>x!==slot.main); const sec=secs[rand(0,secs.length-1)]; stats[sec]=Math.round(budget*(0.3+Math.random()*0.4)); }
  if(rarity==="epic"||rarity==="legendary"){ const secs=Object.keys(CORE_STATS).filter(x=>x!==slot.main&&!stats[x]); if(secs.length){ const s2=secs[rand(0,secs.length-1)]; stats[s2]=Math.round(budget*(0.2+Math.random()*0.3)); } }
  return { id:uid(), name:base, slot:slot.id, rarity, ilvl, stats, value:Math.round(budget*1.6*(RARITY_ORDER.indexOf(rarity)+1)) };
}
const itemCoreSum=(it)=>it?Object.values(it.stats).reduce((a,b)=>a+b,0):0;
const xpForLevel=(lvl)=>Math.round(80*Math.pow(lvl,1.55));

// derive combat stats from core stats
function deriveCombat(core){
  return {
    atk: Math.round(core.str*2.2 + core.agi*0.5),
    def: Math.round(core.vit*2.0 + core.str*0.4),
    mag: Math.round(core.int*2.3),
    spd: Math.round(core.agi*1.8 + core.int*0.3),
    hp:  Math.round(core.vit*12 + 80),
    crit: Math.min(60, Math.round(core.agi*0.6 + 3)),
  };
}
const COMBAT_META={ atk:{n:"Attack",c:"#ff7a6b",i:"⚔"}, def:{n:"Defense",c:"#7fb8ff",i:"🛡"}, mag:{n:"Magic",c:"#c89bff",i:"✦"}, spd:{n:"Speed",c:"#7fe8b0",i:"➤"}, hp:{n:"Health",c:"#ff5577",i:"❤"}, crit:{n:"Crit %",c:"#ffd76b",i:"✸"} };

const STARTER=()=>({
  name:null, race:null,
  level:1, xp:0, gold:120, crystals:8,
  equipped:{ weapon:null, head:null, chest:null, hands:null, legs:null, ring:null, amulet:null, boots:null },
  inventory:[],
  active:null,
  achievements:{}, claimedPacks:{},
  stats:{ missions:0, goldEarned:0, legendaries:0, speedups:0, voidClears:0 },
});

// ============================================================
function GameInner({ me, onNeedLogin, onSyncProfile, onJuice }){
  const [game,setGame]=useState(()=>{ try{ const s=localStorage.getItem("rasmusten_save"); if(s) return JSON.parse(s); }catch(e){} return STARTER(); });
  const [tab,setTab]=useState("missions");
  const [nameInput,setNameInput]=useState("");
  const [pickRace,setPickRace]=useState(null);
  const [now,setNow]=useState(Date.now());
  const [toasts,setToasts]=useState([]);
  const [lootModal,setLootModal]=useState(null);
  const toastId=useRef(0);

  useEffect(()=>{ const t=setInterval(()=>setNow(Date.now()),250); return ()=>clearInterval(t); },[]);
  useEffect(()=>{ try{ localStorage.setItem("rasmusten_save",JSON.stringify(game)); }catch(e){} },[game]);

  const pushToast=useCallback((msg,color="#ffd76b",icon="✦")=>{
    const id=++toastId.current;
    setToasts(t=>[...t,{id,msg,color,icon}]);
    setTimeout(()=>setToasts(t=>t.filter(x=>x.id!==id)),3500);
  },[]);

  const race=useMemo(()=>RACES.find(r=>r.id===game.race),[game.race]);

  // total core stats = race base + level growth + equipment
  const coreStats=useMemo(()=>{
    const c={str:0,agi:0,int:0,vit:0};
    if(race){ for(const k in race.base) c[k]=race.base[k]; }
    // level growth scaled by race tendencies
    const grow=(game.level-1);
    if(race){ for(const k in race.base){ c[k]+=Math.round(grow*(race.base[k]/12)*1.1); } }
    Object.values(game.equipped).forEach(it=>{ if(it) for(const k in it.stats){ if(c[k]!==undefined) c[k]+=it.stats[k]; } });
    return c;
  },[race,game.level,game.equipped]);

  const combat=useMemo(()=>deriveCombat(coreStats),[coreStats]);
  const power=useMemo(()=>Math.round(combat.atk*1.2+combat.def*1.0+combat.mag*1.1+combat.spd*0.9+combat.hp*0.15+combat.crit*2),[combat]);

  // Persist computed power for backend sync + trigger juice on big jumps
  useEffect(() => {
    try {
      const raw = localStorage.getItem("rasmusten_save");
      if (raw) { const g = JSON.parse(raw); g.lastPower = power; localStorage.setItem("rasmusten_save", JSON.stringify(g)); }
    } catch {}
  }, [power]);

  // Auto-sync to leaderboard (unique name enforced server-side)
  useEffect(() => {
    if (!me || !onSyncProfile || !game.name || !game.race) return;
    const t = setTimeout(async () => {
      try { await onSyncProfile({ characterName: game.name, race: game.race, level: game.level, power }); }
      catch (e) {
        const msg = String(e?.message || e);
        if (msg.includes("taken")) pushToast("Name taken on leaderboard — please rename.", "#ff7a6b", "✖");
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [me, onSyncProfile, game.name, game.race, game.level, power]);


  // achievements
  useEffect(()=>{
    if(!game.name) return;
    let gained=[];
    setGame(g=>{
      const ach={...g.achievements}; let crystals=g.crystals;
      ACHIEVEMENTS.forEach(a=>{ if(!ach[a.id]&&a.check(g,power)){ ach[a.id]=true; crystals+=a.crystals; gained.push(a); } });
      if(!gained.length) return g;
      return {...g,achievements:ach,crystals};
    });
    gained.forEach(a=>pushToast(`Achievement: ${a.name} (+${a.crystals} 💎)`,"#ffd76b","🏆"));
  },[game.stats,game.level,game.equipped,power,game.name,pushToast]);

  useEffect(()=>{ if(game.active&&now>=game.active.endsAt) completeMission(); /*eslint-disable-line*/ },[now]);

  function effectiveMissionDurationMs(m){
    // Difficulty vs hero strength: stronger heroes finish faster.
    // We keep a floor so missions never become instant for free.
    const diff = (m.lvl * 55) + (m.tier * 160) + (m.minutes * 40);
    const hero = (power * 0.9) + (game.level * 60) + (combat.spd * 6) + (combat.atk * 1.5);
    const ratio = hero / Math.max(1, diff);
    // ratio 1.0 => baseline; ratio 2.0 => ~0.65x time; ratio 0.5 => ~1.35x time
    const speedMult = clamp(1.25 - 0.35 * Math.log2(Math.max(0.25, ratio)), 0.45, 1.6);
    const base = m.minutes * 60 * 1000;
    return Math.round(base * speedMult);
  }

  function startMission(m){
    if(game.active) return pushToast("Hero is already on a mission!","#ff7a6b","✖");
    if(game.level<m.lvl) return pushToast(`Requires Level ${m.lvl}.`,"#ff7a6b","🔒");
    const dur = effectiveMissionDurationMs(m);
    setGame(g=>({...g,active:{missionId:m.id,startedAt:Date.now(),endsAt:Date.now()+dur, baseMinutes:m.minutes}}));
    pushToast(`${game.name} departs for ${m.name}!`,"#7fe8b0","🗺");
  }  
  function completeMission(){
    setGame(g=>{
      if(!g.active) return g;
      const m=MISSIONS.find(x=>x.id===g.active.missionId);
      const gold=rand(m.gold[0],m.gold[1]);
      const xp=Math.round(m.lvl*18+m.tier*25);
      const drops=[]; const numDrops=1+(Math.random()<0.35+m.tier*0.07?1:0); let legend=0;
      for(let i=0;i<numDrops;i++){ const it=generateItem(m); if(it.rarity==="legendary") legend++; drops.push(it); }
      let level=g.level, curXp=g.xp+xp;
      while(curXp>=xpForLevel(level)){ curXp-=xpForLevel(level); level++; }
      const ns={...g.stats, missions:g.stats.missions+1, goldEarned:g.stats.goldEarned+gold, legendaries:g.stats.legendaries+legend, voidClears:g.stats.voidClears+(m.id==="m9"?1:0)};
      setTimeout(()=>setLootModal({mission:m,gold,xp,drops,leveledTo:level>g.level?level:null}),50);
      return {...g,active:null,gold:g.gold+gold,xp:curXp,level,inventory:[...g.inventory,...drops],stats:ns};
    });
  }
  function speedUp(){
    if(!game.active) return;
    const remaining=Math.max(0,game.active.endsAt-now);
    const cost=Math.max(1,Math.ceil(remaining/60000));
    if(game.crystals<cost) return pushToast(`Need ${cost} 💎 to rush.`,"#ff7a6b","💎");
    setGame(g=>({...g,crystals:g.crystals-cost,active:{...g.active,endsAt:Date.now()-1},stats:{...g.stats,speedups:g.stats.speedups+1}}));
    pushToast(`Rushed for ${cost} 💎!`,"#c89bff","⚡");
  }
  function equip(item){
    setGame(g=>{
      const prev=g.equipped[item.slot];
      const inv=g.inventory.filter(i=>i.id!==item.id);
      if(prev) inv.push(prev);
      return {...g,equipped:{...g.equipped,[item.slot]:item},inventory:inv};
    });
    pushToast(`Equipped: ${item.name}`, RARITIES[item.rarity].color, "🧰");
  }
  function unequip(slot){
    setGame(g=>{ const it=g.equipped[slot]; if(!it) return g; return {...g,equipped:{...g.equipped,[slot]:null},inventory:[...g.inventory,it]}; });
    pushToast("Unequipped gear.", "#a89770", "🎽");
  }
  function sell(item){ setGame(g=>({...g,gold:g.gold+item.value,inventory:g.inventory.filter(i=>i.id!==item.id),stats:{...g.stats,goldEarned:g.stats.goldEarned+item.value}})); pushToast(`Sold ${item.name} for ${item.value} gold.`,"#ffd76b","🪙"); }
  function buyPack(p){
    if(p.oneTime){ if(game.claimedPacks[p.id]) return pushToast("Already claimed!","#ff7a6b","✖"); setGame(g=>({...g,crystals:g.crystals+p.crystals,claimedPacks:{...g.claimedPacks,[p.id]:true}})); return pushToast(`Claimed ${p.crystals} 💎!`,"#c89bff","💎"); }
    if(game.gold<p.goldCost) return pushToast("Not enough gold.","#ff7a6b","🪙");
    setGame(g=>({...g,gold:g.gold-p.goldCost,crystals:g.crystals+p.crystals})); pushToast(`+${p.crystals} 💎`,"#c89bff","💎");
  }
  function resetGame(){ if(confirm("Wipe all progress and start over?")){ localStorage.removeItem("rasmusten_save"); setGame(STARTER()); setPickRace(null); setNameInput(""); } }

  // ---------- Character creation ----------
  if(!game.name||!game.race){
    return (
      <div className="rasm-root" style={S.bg}>
        <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{...S.panel,maxWidth:880,width:"100%",padding:"36px 32px",textAlign:"center"}}>
            <div style={{fontSize:54}}>🐉</div>
            <h1 className="cinzel legendary-shimmer" style={{fontSize:46,margin:"4px 0 0",fontWeight:900,letterSpacing:2}}>RASMUSTEN</h1>
            <p className="medieval" style={{color:"#bfa971",fontSize:17,marginTop:4}}>Forge your champion, then claim your name.</p>
            <div style={{height:1,background:"linear-gradient(90deg,transparent,#5a4a2a,transparent)",margin:"22px 0"}}/>
            <h3 className="cinzel" style={{color:"#ffd76b",margin:"0 0 16px"}}>Choose Your Race</h3>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:14}}>
              {RACES.map(r=>(
                <div key={r.id} className="hero-card btn-press" onClick={()=>setPickRace(r.id)}
                  style={{cursor:"pointer",background:"rgba(0,0,0,0.3)",border:`2px solid ${pickRace===r.id?r.color:"#3a2f1a"}`,borderRadius:14,overflow:"hidden",
                    boxShadow:pickRace===r.id?`0 0 22px ${r.color}66`:"none"}}>
                  <img src={r.img} alt={r.name} style={{width:"100%",aspectRatio:"1",objectFit:"cover",display:"block"}}/>
                  <div style={{padding:"8px 8px 12px"}}>
                    <div className="cinzel" style={{fontWeight:700,fontSize:13,color:r.color}}>{r.name}</div>
                    <div style={{display:"flex",justifyContent:"center",gap:6,marginTop:6,flexWrap:"wrap"}}>
                      {Object.entries(r.base).map(([k,v])=><span key={k} style={{fontSize:10,color:CORE_STATS[k].c}}>{CORE_STATS[k].i}{v}</span>)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {pickRace&&(
              <div style={{marginTop:14,color:"#a89770",fontSize:13,fontStyle:"italic"}}>
                "{RACES.find(r=>r.id===pickRace).blurb}"
              </div>
            )}
            <input value={nameInput} onChange={e=>setNameInput(e.target.value.slice(0,16))}
              onKeyDown={e=>e.key==="Enter"&&nameInput.trim()&&pickRace&&setGame(g=>({...g,name:nameInput.trim(),race:pickRace}))}
              placeholder="Name your hero..." style={{...S.input,marginTop:18}}/>
            <button className="btn-press" disabled={!nameInput.trim()||!pickRace}
              onClick={()=>setGame(g=>({...g,name:nameInput.trim(),race:pickRace}))}
              style={{...S.btnPrimary,width:"100%",marginTop:14,opacity:(nameInput.trim()&&pickRace)?1:0.4,cursor:(nameInput.trim()&&pickRace)?"pointer":"not-allowed"}}>
              ⚔ Begin Your Legend
            </button>
            <p style={{color:"#8a7a55",fontSize:12,marginTop:14}}>Start with 120 🪙 gold and 8 💎 crystals.</p>
          </div>
        </div>
      </div>
    );
  }

  const activeMission=game.active?MISSIONS.find(m=>m.id===game.active.missionId):null;
  const remaining=game.active?Math.max(0,game.active.endsAt-now):0;
  const totalDur=game.active?game.active.endsAt-game.active.startedAt:1;
  const effectiveMin = game.active ? Math.round((totalDur/60000)*10)/10 : null;
  const progress=game.active?Math.min(100,(1-remaining/totalDur)*100):0;
  const unlockedAch=ACHIEVEMENTS.filter(a=>game.achievements[a.id]).length;

  return (
    <div className="rasm-root" style={S.bg}>
      <div style={{position:"fixed",top:16,right:16,zIndex:100,display:"flex",flexDirection:"column",gap:8,maxWidth:320}}>
        {toasts.map(t=>(<div key={t.id} className="toast" style={{...S.toast,borderColor:t.color}}><span style={{fontSize:18}}>{t.icon}</span><span style={{color:t.color,fontWeight:600,fontSize:14}}>{t.msg}</span></div>))}
      </div>

      {lootModal&&<LootModal data={lootModal} onClose={()=>setLootModal(null)} onEquip={equip}/>}

      <header style={S.header}>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <div style={{fontSize:34}}>🐉</div>
          <div>
            <div className="cinzel legendary-shimmer" style={{fontSize:26,fontWeight:900,lineHeight:1}}>RASMUSTEN</div>
            <div className="medieval" style={{color:"#bfa971",fontSize:12}}>the realm of {game.name}</div>
          </div>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
          <Resource icon="🪙" value={game.gold.toLocaleString()} color="#ffd76b"/>
          <Resource icon="💎" value={game.crystals} color="#9bd4ff"/>
          <Resource icon="⚡" value={power.toLocaleString()} color="#ff9a1a" label="Power"/>
        </div>
      </header>

      <div style={S.heroBar}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <img src={race.img} alt="" style={{width:46,height:46,borderRadius:"50%",objectFit:"cover",border:`2px solid ${race.color}`}}/>
          <div>
            <div className="cinzel" style={{fontSize:18,fontWeight:700}}>{game.name}</div>
            <div style={{fontSize:12,color:race.color}}>Lvl {game.level} {race.name}</div>
          </div>
        </div>
        <div style={{flex:1,maxWidth:340,minWidth:160}}>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#bfa971",marginBottom:3}}><span>XP</span><span>{game.xp} / {xpForLevel(game.level)}</span></div>
          <div style={S.xpTrack}><div style={{...S.xpFill,width:`${Math.min(100,(game.xp/xpForLevel(game.level))*100)}%`}}/></div>
        </div>
        <div style={{display:"flex",gap:12,fontSize:13,flexWrap:"wrap"}}>
          {Object.entries(coreStats).map(([k,v])=><span key={k} style={{color:CORE_STATS[k].c,fontWeight:600}}>{CORE_STATS[k].i} {v}</span>)}
        </div>
      </div>

      {activeMission&&(
        <div style={S.activeBanner}>
          <div style={{display:"flex",alignItems:"center",gap:12,flex:1,minWidth:220}}>
            <img src={activeMission.img} alt="" style={{width:54,height:36,borderRadius:6,objectFit:"cover",border:`1px solid ${TIER_COLORS[activeMission.tier]}`}}/>
            <div style={{flex:1}}>
              <div className="cinzel" style={{fontWeight:700,color:TIER_COLORS[activeMission.tier]}}>{activeMission.name}</div>
              <div style={S.progTrack}><div style={{...S.progFill,width:`${progress}%`,background:TIER_COLORS[activeMission.tier]}}/></div>
              <div style={{fontSize:11,color:"#a89770",marginTop:4}}>
                Effective time: <span style={{color:"#ffe9c0",fontWeight:700}}>{effectiveMin}m</span>
                {game.active?.baseMinutes ? <span style={{color:"#8a7a55"}}> (base {game.active.baseMinutes}m)</span> : null}
              </div>
            </div>
          </div>
          <div className="cinzel" style={{fontSize:20,fontWeight:700,minWidth:80,textAlign:"center"}}>{fmtTime(remaining)}</div>
          <button className="btn-press" onClick={speedUp} style={S.rushBtn}>⚡ Rush ({Math.max(1,Math.ceil(remaining/60000))} 💎)</button>
        </div>
      )}

      <nav style={S.tabs}>
        {[["missions","🗺️ Missions"],["hero","🧙 Hero"],["bag","🎒 Inventory"],["achievements","🏆 Achievements"],["leaderboard","🏅 Leaderboard"],["store","💎 Store"]].map(([id,label])=>(
          <button key={id} className="btn-press" onClick={()=>setTab(id)} style={{...S.tab,...(tab===id?S.tabActive:{})}}>
            {label}{id==="achievements"&&<span style={S.badge}>{unlockedAch}/{ACHIEVEMENTS.length}</span>}
            {id==="bag"&&game.inventory.length>0&&<span style={S.badge}>{game.inventory.length}</span>}
          </button>
        ))}
      </nav>

      <main className="scrollbar" style={S.main}>
        {tab==="missions"&&(
          <div style={S.grid}>
            {MISSIONS.map(m=>{
              const locked=game.level<m.lvl, busy=!!game.active, isThis=game.active?.missionId===m.id;
              return (
                <div key={m.id} style={{...S.card,padding:0,overflow:"hidden",opacity:locked?0.6:1,borderColor:isThis?TIER_COLORS[m.tier]:"#3a2f1a"}}>
                  <div style={{position:"relative",height:140}}>
                    <img src={m.img} alt="" style={{width:"100%",height:"100%",objectFit:"cover",filter:locked?"grayscale(0.7)":"none"}}/>
                    <div style={{position:"absolute",inset:0,background:"linear-gradient(180deg,transparent 40%,rgba(15,11,6,0.96))"}}/>
                    <span style={{...S.tierTag,position:"absolute",top:8,left:8,background:TIER_COLORS[m.tier]+"cc",color:"#15100a",borderColor:TIER_COLORS[m.tier]}}>TIER {m.tier}</span>
                    <span style={{position:"absolute",top:8,right:8,fontSize:12,fontWeight:700,color:locked?"#ff7a6b":"#ffe9c0",background:"rgba(0,0,0,0.6)",padding:"2px 8px",borderRadius:6}}>{locked?`🔒 Lvl ${m.lvl}`:`Lvl ${m.lvl}+`}</span>
                    <div className="cinzel" style={{position:"absolute",bottom:8,left:12,right:12,fontSize:16,fontWeight:700,textShadow:"0 2px 6px #000"}}>{m.name}</div>
                  </div>
                  <div style={{padding:"12px 16px 16px"}}>
                    <div style={{fontSize:13,color:"#a89770",minHeight:36,lineHeight:1.4}}>{m.desc}</div>
                    <div style={{display:"flex",gap:14,fontSize:13,margin:"10px 0",color:"#bfa971"}}><span>⏱ {fmtMin(m.minutes)}</span><span>🪙 {m.gold[0]}–{m.gold[1]}</span><span>🎁 Gear</span></div>
                    <button className="btn-press" disabled={locked||busy} onClick={()=>startMission(m)}
                      style={{...S.btnPrimary,width:"100%",opacity:(locked||busy)?0.4:1,cursor:(locked||busy)?"not-allowed":"pointer",...(isThis?{background:"#3a5a3a",borderColor:"#7fe8b0"}:{})}}>
                      {isThis?"⚔ In Progress…":busy?"Hero is busy":locked?"Locked":"⚔ Send Hero"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab==="hero"&&(
          <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
            {/* Hero visual with gear overlay */}
            <div style={{...S.panel,flex:"1 1 300px",padding:20}}>
              <h3 className="cinzel" style={S.h3}>⚜ {game.name}</h3>
              <div style={{position:"relative",width:"100%",maxWidth:340,margin:"0 auto",aspectRatio:"1",borderRadius:14,overflow:"hidden",border:`2px solid ${race.color}`,boxShadow:`0 0 30px ${race.color}44`}}>
                <img src={race.img} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                {/* Equipped gear badges layered on the hero */}
                {SLOTS.map(sl=>{
                  const it=game.equipped[sl.id]; if(!it) return null;
                  const L=sl.layer;
                  return (
                    <div key={sl.id} title={`${it.name} (${RARITIES[it.rarity].name})`}
                      style={{position:"absolute",top:L.t,bottom:L.b,left:L.l,right:L.r,width:L.s,height:L.s,
                        transform:`${L.tr||""} ${L.rot?`rotate(${L.rot})`:""}`.trim(),zIndex:L.z,
                        display:"flex",alignItems:"center",justifyContent:"center",fontSize:`calc(${L.s} * 0.55)`,
                        filter:`drop-shadow(0 0 6px ${RARITIES[it.rarity].color})`,
                        ...(it.rarity==="legendary"?{animation:"glowpulse 1.6s infinite",color:RARITIES[it.rarity].color}:{})}}>
                      {sl.icon}
                    </div>
                  );
                })}
                <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"20px 10px 8px",background:"linear-gradient(transparent,rgba(0,0,0,0.85))",textAlign:"center"}}>
                  <span className="cinzel" style={{color:race.color,fontWeight:700,fontSize:13}}>Lvl {game.level} · ⚡{power}</span>
                </div>
              </div>
              <div style={{textAlign:"center",fontSize:12,color:"#8a7a55",marginTop:8}}>Equipped gear glows on your hero ✦</div>
            </div>

            {/* Equipment slots */}
            <div style={{...S.panel,flex:"1 1 300px",padding:20}}>
              <h3 className="cinzel" style={S.h3}>⚔ Equipment</h3>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9}}>
                {SLOTS.map(sl=>{
                  const it=game.equipped[sl.id];
                  return (
                    <div key={sl.id} style={{...S.slot,borderColor:it?RARITIES[it.rarity].color:"#3a2f1a"}} onClick={()=>it&&unequip(sl.id)} title={it?"Click to unequip":""}>
                      <span style={{fontSize:22}}>{sl.icon}</span>
                      <div style={{flex:1,overflow:"hidden"}}>
                        <div style={{fontSize:10,color:"#8a7a55",textTransform:"uppercase"}}>{sl.name}</div>
                        {it?<>
                          <div style={{fontSize:13,fontWeight:600,color:RARITIES[it.rarity].color,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{it.name}</div>
                          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{Object.entries(it.stats).map(([k,v])=><span key={k} style={{fontSize:10,color:CORE_STATS[k].c}}>{CORE_STATS[k].i}{v}</span>)}</div>
                        </>:<div style={{fontSize:12,color:"#5a4a2a"}}>— empty —</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Stats sheet */}
            <div style={{...S.panel,flex:"1 1 240px",padding:20}}>
              <h3 className="cinzel" style={S.h3}>📊 Attributes</h3>
              {Object.entries(coreStats).map(([k,v])=>(
                <div key={k} style={{marginBottom:8}}>
                  <StatRow label={CORE_STATS[k].n} value={v} color={CORE_STATS[k].c} icon={CORE_STATS[k].i}/>
                  <div style={{fontSize:10,color:"#7a6a45",marginTop:-2}}>{CORE_STATS[k].d}</div>
                </div>
              ))}
              <div style={{height:1,background:"#3a2f1a",margin:"14px 0"}}/>
              <h3 className="cinzel" style={{...S.h3,marginBottom:10}}>⚔ Combat</h3>
              {Object.entries(combat).map(([k,v])=><StatRow key={k} label={COMBAT_META[k].n} value={k==="crit"?v+"%":v} color={COMBAT_META[k].c} icon={COMBAT_META[k].i}/>)}
              <div style={{height:1,background:"#3a2f1a",margin:"14px 0"}}/>
              <StatRow label="Missions" value={game.stats.missions} color="#bfa971"/>
              <StatRow label="Legendaries" value={game.stats.legendaries} color="#ff9a1a"/>
            </div>
          </div>
        )}

        {tab==="bag"&&(
          game.inventory.length===0?<div style={S.empty}>🎒 Your bag is empty. Complete missions to find gear!</div>:(
            <div style={S.grid}>
              {[...game.inventory].sort((a,b)=>RARITY_ORDER.indexOf(b.rarity)-RARITY_ORDER.indexOf(a.rarity)).map(it=>{
                const eq=game.equipped[it.slot]; const diff=itemCoreSum(it)-itemCoreSum(eq);
                return (
                  <div key={it.id} style={{...S.card,borderColor:RARITIES[it.rarity].color,padding:16}}>
                    <div style={{display:"flex",justifyContent:"space-between"}}>
                      <span style={{fontSize:11,fontWeight:700,color:RARITIES[it.rarity].color,textTransform:"uppercase"}}>{RARITIES[it.rarity].name}</span>
                      <span style={{fontSize:11,color:"#8a7a55"}}>{SLOTS.find(s=>s.id===it.slot)?.icon} {SLOTS.find(s=>s.id===it.slot)?.name}</span>
                    </div>
                    <div className="cinzel" style={{fontSize:16,fontWeight:700,margin:"6px 0",color:RARITIES[it.rarity].color}}>{it.name}</div>
                    <div style={{fontSize:12,color:"#a89770",marginBottom:8}}>Item Level {it.ilvl}</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:10}}>{Object.entries(it.stats).map(([k,v])=><span key={k} style={{fontSize:12,color:CORE_STATS[k].c}}>{CORE_STATS[k].i}+{v}</span>)}</div>
                    {eq&&<div style={{fontSize:11,color:diff>=0?"#7fe8b0":"#ff7a6b",marginBottom:8}}>{diff>=0?"▲":"▼"} {diff>=0?"+":""}{diff} total vs equipped</div>}
                    <div style={{display:"flex",gap:8}}>
                      <button className="btn-press" onClick={()=>equip(it)} style={{...S.btnSm,flex:1,background:"#2a3a4a",borderColor:"#3b9dff",color:"#9bd4ff"}}>Equip</button>
                      <button className="btn-press" onClick={()=>sell(it)} style={{...S.btnSm,background:"#3a2f1a",borderColor:"#5a4a2a",color:"#ffd76b"}}>Sell 🪙{it.value}</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {tab==="achievements"&&(
          <div style={S.grid}>
            {ACHIEVEMENTS.map(a=>{
              const done=game.achievements[a.id];
              return (
                <div key={a.id} style={{...S.card,opacity:done?1:0.7,borderColor:done?"#ffd76b":"#3a2f1a",padding:18}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{fontSize:30,...(done?{}:{filter:"grayscale(1) opacity(0.5)"})}}>{done?"🏆":"🔒"}</div>
                    <div style={{flex:1}}><div className="cinzel" style={{fontWeight:700,fontSize:15,color:done?"#ffd76b":"#a89770"}}>{a.name}</div><div style={{fontSize:12,color:"#a89770"}}>{a.desc}</div></div>
                  </div>
                  <div style={{marginTop:10,fontSize:12,color:done?"#7fe8b0":"#8a7a55",textAlign:"right"}}>{done?"✓ Claimed":"Reward:"} +{a.crystals} 💎</div>
                </div>
              );
            })}
          </div>
        )}

        {tab==="store"&&(
          <div>
            <div style={{...S.panel,padding:20,marginBottom:20,textAlign:"center"}}>
              <h2 className="cinzel legendary-shimmer" style={{fontSize:28,margin:"0 0 6px"}}>💎 The Crystal Bazaar</h2>
              <p style={{color:"#a89770",margin:0}}>Crystals rush missions instantly. Earn them from achievements, or trade gold here.</p>
            </div>
            <div style={S.grid}>
              {CRYSTAL_PACKS.map(p=>{
                const claimed=p.oneTime&&game.claimedPacks[p.id];
                return (
                  <div key={p.id} style={{...S.card,padding:22,textAlign:"center",borderColor:p.oneTime?"#7fe8b0":"#9bd4ff"}}>
                    <div style={{fontSize:40}}>💎</div>
                    <div className="cinzel" style={{fontSize:26,fontWeight:900,color:"#9bd4ff"}}>{p.crystals}</div>
                    <div style={{fontSize:13,color:"#a89770",margin:"6px 0 14px",minHeight:36}}>{p.label}</div>
                    <button className="btn-press" disabled={claimed} onClick={()=>buyPack(p)} style={{...S.btnPrimary,width:"100%",opacity:claimed?0.4:1,cursor:claimed?"default":"pointer",...(p.oneTime?{background:"#2a4a3a",borderColor:"#7fe8b0"}:{})}}>
                      {claimed?"✓ Claimed":p.oneTime?"🎁 Claim Free":`🪙 ${p.goldCost.toLocaleString()} Gold`}
                    </button>
                  </div>
                );
              })}
            </div>
            <div style={{textAlign:"center",marginTop:24}}>
              <button className="btn-press" onClick={resetGame} style={{...S.btnSm,background:"#3a1a1a",borderColor:"#7a3a3a",color:"#ff9a9a",padding:"8px 16px"}}>↺ Reset Progress</button>
            </div>
          </div>
        )}
      
        {tab==="leaderboard"&&(<LeaderboardPanel me={me} myName={game.name} />)}
</main>

      <footer style={{textAlign:"center",padding:14,color:"#5a4a2a",fontSize:12}} className="medieval">⚔ Rasmusten — may your legend echo through the ages, {game.name}. ⚔</footer>
    </div>
  );
}

function Resource({icon,value,color,label}){
  return (<div style={{display:"flex",alignItems:"center",gap:6,background:"rgba(20,14,6,0.7)",border:"1px solid #3a2f1a",borderRadius:10,padding:"6px 12px"}}>
    <span style={{fontSize:16}}>{icon}</span><span className="cinzel" style={{color,fontWeight:700}}>{value}</span>{label&&<span style={{fontSize:11,color:"#8a7a55"}}>{label}</span>}</div>);
}
function StatRow({label,value,color,icon,big}){
  return (<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:big?"6px 0":"3px 0"}}>
    <span style={{color:"#a89770",fontSize:big?16:14}}>{icon} {label}</span><span className="cinzel" style={{color,fontWeight:700,fontSize:big?26:15}}>{value}</span></div>);
}
function LootModal({data,onClose,onEquip}){
  return (<div style={S.overlay} onClick={onClose}>
    <div style={{...S.panel,maxWidth:480,width:"100%",padding:30,textAlign:"center",animation:"pop 0.3s ease"}} onClick={e=>e.stopPropagation()}>
      <div style={{fontSize:46}}>🎉</div>
      <h2 className="cinzel" style={{margin:"4px 0",color:"#ffd76b"}}>Mission Complete!</h2>
      <div style={{color:"#a89770",fontSize:14}}>{data.mission.name}</div>
      {data.leveledTo&&<div className="cinzel" style={{margin:"10px 0",fontSize:18,animation:"glowpulse 1.5s infinite",color:"#7fe8b0"}}>⬆ LEVEL UP! → Level {data.leveledTo}</div>}
      <div style={{display:"flex",justifyContent:"center",gap:24,margin:"16px 0"}}>
        <div><div style={{fontSize:22}}>🪙</div><div className="cinzel" style={{color:"#ffd76b",fontWeight:700}}>+{data.gold}</div></div>
        <div><div style={{fontSize:22}}>✦</div><div className="cinzel" style={{color:"#9bd4ff",fontWeight:700}}>+{data.xp} XP</div></div>
      </div>
      <div style={{height:1,background:"#3a2f1a",margin:"6px 0 16px"}}/>
      <div style={{fontSize:13,color:"#a89770",marginBottom:10}}>Loot acquired:</div>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {data.drops.map(it=>(
          <div key={it.id} style={{display:"flex",alignItems:"center",gap:12,background:"rgba(0,0,0,0.3)",border:`1px solid ${RARITIES[it.rarity].color}`,borderRadius:10,padding:"10px 14px",...(it.rarity==="legendary"?{animation:"glowpulse 1.5s infinite",color:RARITIES[it.rarity].color}:{})}}>
            <span style={{fontSize:22}}>{SLOTS.find(s=>s.id===it.slot)?.icon}</span>
            <div style={{flex:1,textAlign:"left"}}><div style={{fontWeight:700,color:RARITIES[it.rarity].color}}>{it.name}</div><div style={{fontSize:11,color:"#8a7a55"}}>{RARITIES[it.rarity].name} · {Object.entries(it.stats).map(([k,v])=>`${CORE_STATS[k].i}${v}`).join(" ")}</div></div>
            <button className="btn-press" onClick={()=>onEquip(it)} style={{...S.btnSm,background:"#2a3a4a",borderColor:"#3b9dff",color:"#9bd4ff"}}>Equip</button>
          </div>
        ))}
      </div>
      <button className="btn-press" onClick={onClose} style={{...S.btnPrimary,width:"100%",marginTop:20}}>Continue Adventure</button>
    </div>
  </div>);
}

function fmtTime(ms){ const s=Math.ceil(ms/1000); const m=Math.floor(s/60); const sec=s%60; return `${m}:${String(sec).padStart(2,"0")}`; }
function fmtMin(min){ if(min<1) return `${Math.round(min*60)}s`; return min<60?`${min}m`:`${Math.floor(min/60)}h ${min%60}m`; }

const S={
  bg:{minHeight:"100vh",background:"radial-gradient(ellipse at top,#2a1f14 0%,#14100a 50%,#0a0805 100%)",backgroundAttachment:"fixed"},
  panel:{background:"linear-gradient(165deg,rgba(40,30,18,0.95),rgba(22,16,9,0.95))",border:"1px solid #5a4a2a",borderRadius:16,boxShadow:"0 8px 40px rgba(0,0,0,0.6),inset 0 1px 0 rgba(255,200,120,0.08)"},
  input:{width:"100%",padding:"14px 18px",fontSize:17,fontFamily:"'Spectral',serif",background:"rgba(0,0,0,0.4)",border:"1px solid #5a4a2a",borderRadius:10,color:"#ffe9c0",outline:"none",textAlign:"center"},
  btnPrimary:{padding:"12px 20px",fontFamily:"'Cinzel',serif",fontWeight:700,fontSize:15,background:"linear-gradient(180deg,#6b3f1a,#4a2a10)",color:"#ffe9c0",border:"1px solid #a86b30",borderRadius:10,cursor:"pointer",boxShadow:"0 3px 0 #2a1808,inset 0 1px 0 rgba(255,200,120,0.2)"},
  btnSm:{padding:"7px 10px",fontFamily:"'Cinzel',serif",fontWeight:600,fontSize:12,borderRadius:8,border:"1px solid",cursor:"pointer",background:"transparent"},
  header:{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 24px",flexWrap:"wrap",gap:12,borderBottom:"1px solid #3a2f1a",background:"rgba(10,8,4,0.6)",position:"sticky",top:0,zIndex:50,backdropFilter:"blur(8px)"},
  heroBar:{display:"flex",alignItems:"center",gap:20,padding:"12px 24px",flexWrap:"wrap",borderBottom:"1px solid #2a2114",background:"rgba(20,14,8,0.4)"},
  xpTrack:{height:8,background:"rgba(0,0,0,0.5)",borderRadius:4,overflow:"hidden",border:"1px solid #3a2f1a"},
  xpFill:{height:"100%",background:"linear-gradient(90deg,#3b9dff,#9bd4ff)",transition:"width 0.4s"},
  activeBanner:{display:"flex",alignItems:"center",gap:16,padding:"14px 24px",flexWrap:"wrap",background:"linear-gradient(90deg,rgba(60,40,20,0.6),rgba(30,20,10,0.4))",borderBottom:"1px solid #5a4a2a"},
  progTrack:{height:10,background:"rgba(0,0,0,0.5)",borderRadius:5,overflow:"hidden",marginTop:5,border:"1px solid #3a2f1a"},
  progFill:{height:"100%",transition:"width 0.4s",boxShadow:"0 0 10px currentColor"},
  rushBtn:{padding:"10px 16px",fontFamily:"'Cinzel',serif",fontWeight:700,fontSize:13,background:"linear-gradient(180deg,#5a3a7a,#3a2050)",color:"#e0c0ff",border:"1px solid #c084fc",borderRadius:10,cursor:"pointer",boxShadow:"0 3px 0 #1a0a30"},
  tabs:{display:"flex",gap:6,padding:"12px 24px 0",flexWrap:"wrap"},
  tab:{padding:"9px 16px",fontFamily:"'Cinzel',serif",fontWeight:600,fontSize:13,background:"rgba(30,22,12,0.6)",color:"#a89770",border:"1px solid #3a2f1a",borderBottom:"none",borderRadius:"10px 10px 0 0",cursor:"pointer",position:"relative"},
  tabActive:{background:"linear-gradient(180deg,rgba(60,45,25,0.95),rgba(30,22,12,0.9))",color:"#ffe9c0",borderColor:"#5a4a2a"},
  badge:{marginLeft:6,fontSize:10,background:"#a86b30",color:"#14100a",borderRadius:8,padding:"1px 6px",fontWeight:700},
  main:{padding:24,borderTop:"1px solid #5a4a2a",minHeight:"50vh"},
  grid:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:16},
  card:{background:"linear-gradient(165deg,rgba(40,30,18,0.9),rgba(22,16,9,0.9))",border:"1px solid #3a2f1a",borderRadius:14,padding:18,boxShadow:"0 4px 20px rgba(0,0,0,0.4)"},
  tierTag:{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:6,border:"1px solid",letterSpacing:0.5},
  slot:{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"rgba(0,0,0,0.3)",border:"1px solid",borderRadius:10,cursor:"pointer"},
  h3:{fontSize:16,margin:"0 0 16px",color:"#ffd76b",fontWeight:700},
  empty:{textAlign:"center",padding:"60px 20px",color:"#8a7a55",fontSize:16},
  toast:{display:"flex",alignItems:"center",gap:10,background:"rgba(20,14,6,0.96)",border:"1px solid",borderRadius:10,padding:"10px 14px",boxShadow:"0 4px 20px rgba(0,0,0,0.6)"},
  overlay:{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:90,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"},
};




async function lbGet(path){ const r=await fetch(path,{credentials:"include"}); if(!r.ok) throw new Error(await r.text()); return r.json(); }
async function lbPost(path,body){ const r=await fetch(path,{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",body:JSON.stringify(body||{})}); if(!r.ok) throw new Error(await r.text()); return r.json(); }

function LeaderboardPanel({ me, myName }){
  const [mode,setMode]=useState("global");
  const [rows,setRows]=useState([]);
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState(null);
  const [friendName,setFriendName]=useState("");
  useEffect(()=>{(async()=>{ setErr(null); try{ setLoading(true); const d=await lbGet(`/api/leaderboard/${mode}?limit=50`); setRows(d.rows||[]);}catch(e){ setErr(String(e?.message||e)); } finally{ setLoading(false);} })();},[mode]);
  async function addFriend(){ setErr(null); try{ await lbPost("/api/friends/add",{characterName:friendName.trim()}); setFriendName(""); const d=await lbGet(`/api/leaderboard/friends?limit=50`); setRows(d.rows||[]); }catch(e){ setErr(String(e?.message||e)); } }
  return (
    <div style={{...S.panel,padding:20}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
        <h2 className="cinzel" style={{margin:0,color:"#ffd76b"}}>🏅 Leaderboard</h2>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button className="btn-press" onClick={()=>setMode("global")} style={{...S.btnSm,...(mode==="global"?{background:"#2a3a4a",borderColor:"#3b9dff",color:"#9bd4ff"}:{})}}>Global</button>
          <button className="btn-press" disabled={!me} onClick={()=>setMode("friends")} style={{...S.btnSm,opacity:me?1:0.5,...(mode==="friends"?{background:"#2a4a3a",borderColor:"#7fe8b0",color:"#bfffe2"}:{})}}>Friends</button>
        </div>
      </div>
      {!me&&<div style={{color:"#8a7a55",fontSize:12,marginTop:8}}>Sign in with Google (top bar) to appear on the leaderboard and add friends.</div>}
      {mode==="friends"&&me&&(
        <div style={{display:"flex",gap:8,marginTop:12,flexWrap:"wrap"}}>
          <input value={friendName} onChange={e=>setFriendName(e.target.value)} placeholder="Add friend by character name" style={{...S.input,textAlign:"left",maxWidth:320,padding:"10px 12px"}}/>
          <button className="btn-press" onClick={addFriend} disabled={!friendName.trim()} style={{...S.btnPrimary,opacity:friendName.trim()?1:0.5}}>Add</button>
        </div>
      )}
      {err&&<div style={{marginTop:10,color:"#ffb3b3"}}>{err}</div>}
      {loading?<div style={{marginTop:10,color:"#a89770"}}>Loading…</div>:(
        <div style={{marginTop:12,border:"1px solid #3a2f1a",borderRadius:12,overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"70px 1.2fr .8fr .6fr .6fr",gap:10,padding:"10px 12px",background:"rgba(0,0,0,0.35)",fontFamily:"'Cinzel',serif",fontWeight:800,color:"#ffd76b"}}>
            <div>#</div><div>Name</div><div>Race</div><div>Level</div><div>Power</div>
          </div>
          {rows.map((r,i)=>(
            <div key={r.characterName} style={{display:"grid",gridTemplateColumns:"70px 1.2fr .8fr .6fr .6fr",gap:10,padding:"10px 12px",background: r.characterName===myName?"rgba(168,107,48,0.18)":"rgba(0,0,0,0.22)",borderTop:"1px solid rgba(58,47,26,0.75)"}}>
              <div style={{color:"#a89770"}}>{i+1}</div>
              <div style={{fontWeight:800,color: r.characterName===myName?"#ffd76b":"#e8dcc0"}}>{r.characterName}</div>
              <div style={{color:"#a89770"}}>{r.race}</div>
              <div style={{color:"#a89770"}}>{r.level}</div>
              <div className="cinzel" style={{fontWeight:900,color:"#ff9a1a"}}>{r.power}</div>
            </div>
          ))}
          {rows.length===0&&<div style={{padding:12,color:"#8a7a55"}}>No entries yet.</div>}
        </div>
      )}
    </div>
  );
}

export default function Game(props){ return <GameInner {...props} />; }
